import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Fab,
  Autocomplete,
  Grid,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Inventory as ProductIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { TopeDescuentoClienteProducto, TopeDescuentoClienteProductoRequest, ProductoRubro } from '../../types';
import { getEndOfDayISOString } from '../../utils/date';

// Función utilitaria para mostrar la fecha en formato DD/MM/YYYY
function formatFechaLocal(fechaVigencia: string) {
  const date = new Date(fechaVigencia);
  return date.getDate().toString().padStart(2, '0') + '/' +
         (date.getMonth() + 1).toString().padStart(2, '0') + '/' +
         date.getFullYear();
}

const TopesClienteProducto: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTope, setEditingTope] = useState<TopeDescuentoClienteProducto | null>(null);
  const [formData, setFormData] = useState<TopeDescuentoClienteProductoRequest>({
    clienteId: '',
    productoId: '',
    topeDescuento: 0,
    fechaVigencia: new Date().toISOString().split('T')[0],
    activo: true,
  });
  const [topeDescuentoText, setTopeDescuentoText] = useState('');
  const [productoSearch, setProductoSearch] = useState('');
  const [selectedProducto, setSelectedProducto] = useState<ProductoRubro | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [editingProducto, setEditingProducto] = useState<ProductoRubro | null>(null);
  const [filtro, setFiltro] = useState('');
  const [errorMensaje, setErrorMensaje] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Consultar topes por cliente-producto
  const { data: topes = [], isLoading, error } = useQuery({
    queryKey: ['topesClienteProducto'],
    queryFn: () => apiService.obtenerTopesClienteProducto(),
  });

  // Consultar rubros disponibles
  const { data: rubros = [] } = useQuery({
    queryKey: ['rubros'],
    queryFn: () => apiService.obtenerRubrosDisponibles(),
  });

  // Consultar productos con búsqueda
  const { data: productos = [], isLoading: loadingProductos } = useQuery({
    queryKey: ['productos', productoSearch],
    queryFn: () => apiService.obtenerProductos(productoSearch),
    enabled: productoSearch.length >= 2,
  });

  // Crear una lista combinada de productos
  const productosCombinados = editingProducto 
    ? [editingProducto, ...productos.filter(p => p.productoId !== editingProducto.productoId)]
    : productos;

  // Filtrado de topes según el texto ingresado
  const topesFiltrados = topes.filter((tope) => {
    const texto = filtro.toLowerCase();
    return (
      tope.clienteRazonSocial?.toLowerCase().includes(texto) ||
      tope.clienteId?.toLowerCase().includes(texto) ||
      tope.productoDetalle?.toLowerCase().includes(texto) ||
      tope.productoId?.toLowerCase().includes(texto) ||
      String(tope.topeDescuento).toLowerCase().includes(texto) ||
      formatFechaLocal(tope.fechaVigencia).includes(texto) ||
      (tope.activo ? 'activo' : 'inactivo').includes(texto)
    );
  });

  // Mutaciones
  const createMutation = useMutation({
    mutationFn: (data: TopeDescuentoClienteProductoRequest) => apiService.crearTopeClienteProducto(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topesClienteProducto'] });
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: 'Tope de descuento creado exitosamente',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      console.error('Error al crear tope:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido al crear el tope';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TopeDescuentoClienteProductoRequest }) =>
      apiService.actualizarTopeClienteProducto(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['topesClienteProducto'] });
      handleCloseDialog();
      console.log('Tope actualizado exitosamente:', data);
      // Opcional: mostrar mensaje de éxito
      // alert('Tope de descuento actualizado correctamente');
    },
    onError: (error: any) => {
      console.error('Error al actualizar tope:', error);
      // Solo mostrar error si no es un error de red o timeout
      if (error.code !== 'ERR_NETWORK' && !error.message.includes('timeout')) {
        alert(`Error al actualizar tope: ${error.response?.data?.message || error.message}`);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.eliminarTopeClienteProducto(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['topesClienteProducto'] });
      setSnackbar({
        open: true,
        message: 'Tope de descuento eliminado exitosamente',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      console.error('Error al eliminar tope:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido al eliminar el tope';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    },
  });

  const handleOpenDialog = (tope?: TopeDescuentoClienteProducto) => {
    if (tope) {
      setEditingTope(tope);
      setFormData({
        clienteId: tope.clienteId,
        productoId: tope.productoId,
        topeDescuento: tope.topeDescuento,
        fechaVigencia: tope.fechaVigencia.split('T')[0],
        activo: tope.activo,
      });
      setTopeDescuentoText(tope.topeDescuento.toString());
      
      // Crear un objeto producto temporal con los datos que tenemos
      const producto = {
        productoId: tope.productoId,
        productoDetalle: tope.productoDetalle,
        productoPrecioOrden: 0,
        productoStock: 0,
        productoMetadata: ''
      };
      
      setSelectedProducto(producto);
      setEditingProducto(producto);
      setProductoSearch(`${tope.productoId} - ${tope.productoDetalle}`);
    } else {
      setEditingTope(null);
      setFormData({
        clienteId: '',
        productoId: '',
        topeDescuento: 0,
        fechaVigencia: new Date().toISOString().split('T')[0],
        activo: true,
      });
      setTopeDescuentoText('');
      setProductoSearch('');
      setSelectedProducto(null);
      setEditingProducto(null);
    }
    setErrorMensaje(null); // Limpiar mensaje de error al abrir
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTope(null);
    setFormData({
      clienteId: '',
      productoId: '',
      topeDescuento: 0,
      fechaVigencia: new Date().toISOString().split('T')[0],
      activo: true,
    });
    setTopeDescuentoText('');
    setProductoSearch('');
    setSelectedProducto(null);
    setEditingProducto(null);
    setErrorMensaje(null); // Limpiar mensaje de error al cerrar
  };

  const handleProductoChange = (producto: ProductoRubro | null) => {
    setSelectedProducto(producto);
    if (producto) {
      setFormData({ ...formData, productoId: producto.productoId });
      setProductoSearch(producto.productoDetalle);
    } else {
      setFormData({ ...formData, productoId: '' });
      setProductoSearch('');
    }
  };

  const handleProductoInputChange = (value: string) => {
    setProductoSearch(value);
    if (!value) {
      setSelectedProducto(null);
      setFormData({ ...formData, productoId: '' });
    }
  };

  const handleTopeDescuentoChange = (value: string) => {
    setTopeDescuentoText(value);
    
    // Validar que sea un número válido
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setFormData({ ...formData, topeDescuento: numValue });
    } else if (value === '' || value === '.') {
      // Permitir campo vacío o punto decimal
      setFormData({ ...formData, topeDescuento: 0 });
    }
  };

  const handleSubmit = () => {
    setErrorMensaje(null); // Limpiar mensaje de error previa
    // Validar existencia solo si es nuevo
    if (!editingTope) {
      const clienteIdForm = formData.clienteId.trim().toLowerCase();
      const productoIdForm = formData.productoId.trim().toLowerCase();
      const topeExistente = topes.find(
        (tope: TopeDescuentoClienteProducto) =>
          tope.clienteId.trim().toLowerCase() === clienteIdForm &&
          tope.productoId.trim().toLowerCase() === productoIdForm
      );
      if (topeExistente) {
        setErrorMensaje(
          `Ya existe un tope de descuento para el cliente con ID: ${topeExistente.clienteId} y producto: ${topeExistente.productoId}.`
        );
        return;
      }
    }
    const requestData = {
      ...formData,
      fechaVigencia: getEndOfDayISOString(formData.fechaVigencia),
      activo: formData.activo
    };
    
    if (editingTope) {
      updateMutation.mutate({ id: editingTope.id, data: requestData });
    } else {
      createMutation.mutate(requestData);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este tope de descuento?')) {
      deleteMutation.mutate(id);
    }
  };

  // Función utilitaria para comparar solo la fecha (ignorando la hora y zona horaria)
  function isVigente(fechaVigencia: string) {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const vigencia = new Date(fechaVigencia);
    vigencia.setHours(0, 0, 0, 0);
    return vigencia >= hoy;
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error al cargar los topes de descuento por cliente y producto
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Topes de Descuento por Cliente y Producto
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Tope
        </Button>
      </Box>
      {/* Filtro de búsqueda */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Filtrar"
          variant="outlined"
          size="small"
          fullWidth
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="Buscar por cualquier campo..."
        />
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>ID Cliente</TableCell>
              <TableCell>Producto</TableCell>
              <TableCell>ID Producto</TableCell>
              <TableCell align="right">Tope (%)</TableCell>
              <TableCell>Vigencia</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Activo</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topesFiltrados.map((tope) => (
              <TableRow key={tope.id}>
                <TableCell>{tope.clienteRazonSocial}</TableCell>
                <TableCell>{tope.clienteId}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <ProductIcon sx={{ mr: 1, fontSize: 20 }} />
                    <Typography variant="body2">
                      {tope.productoDetalle}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{tope.productoId}</TableCell>
                <TableCell align="right">
                  <Typography variant="h6" color="primary">
                    {tope.topeDescuento}%
                  </Typography>
                </TableCell>
                <TableCell>
                  {formatFechaLocal(tope.fechaVigencia)}
                </TableCell>
                <TableCell>
                  {isVigente(tope.fechaVigencia) ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Vigente"
                      color="success"
                      size="small"
                    />
                  ) : (
                    <Chip
                      icon={<WarningIcon />}
                      label="Vencido"
                      color="warning"
                      size="small"
                    />
                  )}
                </TableCell>
                <TableCell>
                  {tope.activo ? (
                    <Chip label="Activo" color="success" size="small" />
                  ) : (
                    <Chip label="Inactivo" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(tope)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(tope.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTope ? 'Editar Tope de Descuento por Producto' : 'Nuevo Tope de Descuento por Producto'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {(createMutation.error || updateMutation.error) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createMutation.error?.response?.data?.message || 
                 updateMutation.error?.response?.data?.message || 
                 'Error al procesar la solicitud'}
              </Alert>
            )}
            {errorMensaje && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {errorMensaje}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="ID del Cliente"
              value={formData.clienteId}
              onChange={(e) => setFormData({ ...formData, clienteId: e.target.value })}
              margin="normal"
              required
              disabled={!!editingTope}
              error={createMutation.error?.response?.data?.message?.includes('Cliente') || false}
            />
            
            <Autocomplete
              fullWidth
              options={productosCombinados}
              getOptionLabel={(option) => `${option.productoId} - ${option.productoDetalle}`}
              value={selectedProducto}
              onChange={(_, newValue) => handleProductoChange(newValue)}
              inputValue={productoSearch}
              onInputChange={(_, newInputValue) => handleProductoInputChange(newInputValue)}
              loading={loadingProductos}
              disabled={!!editingTope}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar Producto"
                  required
                  margin="normal"
                  error={createMutation.error?.response?.data?.message?.includes('Producto') || false}
                  helperText={createMutation.error?.response?.data?.message?.includes('Producto') ? 
                    'El producto especificado no existe en la base de datos' : 
                    'Escriba para buscar productos (mínimo 2 caracteres)'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProductos ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {option.productoId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.productoDetalle}
                    </Typography>
                    {option.productoPrecioOrden && (
                      <Typography variant="caption" color="text.secondary">
                        Precio: ${option.productoPrecioOrden}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              filterOptions={(x) => x} // Deshabilitar filtrado del lado del cliente
              isOptionEqualToValue={(option, value) => option.productoId === value.productoId}
            />
            
            <TextField
              fullWidth
              label="Tope de Descuento (%)"
              type="text"
              value={topeDescuentoText}
              onChange={(e) => handleTopeDescuentoChange(e.target.value)}
              margin="normal"
              required
              placeholder="0.00"
              helperText="Ingrese un valor entre 0 y 100"
              error={topeDescuentoText !== '' && (isNaN(parseFloat(topeDescuentoText)) || parseFloat(topeDescuentoText) < 0 || parseFloat(topeDescuentoText) > 100)}
            />
            <TextField
              fullWidth
              label="Fecha de Vigencia"
              type="date"
              value={formData.fechaVigencia}
              onChange={(e) => setFormData({ ...formData, fechaVigencia: e.target.value })}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.activo}
                  onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  color="primary"
                />
              }
              label="Tope Activo"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.clienteId || !formData.productoId || createMutation.isPending || updateMutation.isPending || (topeDescuentoText !== '' && (isNaN(parseFloat(topeDescuentoText)) || parseFloat(topeDescuentoText) < 0 || parseFloat(topeDescuentoText) > 100))}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              editingTope ? 'Actualizar' : 'Crear'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* FAB para agregar rápidamente */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenDialog()}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default TopesClienteProducto;
